/**
 * Wave 3 — Tender readiness + ECLE actions (embedded in workspace / register summary).
 */
import React, { useMemo } from 'react';
import { Alert, Card, Col, List, Row, Space, Tag, Typography, Button } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { commercialLifecycleService } from '@/services/commercialLifecycle.service';
import { formatDate } from '@/utils/format';
import { PursuitNextActions } from './PursuitNextActions';
import { buildPursuitViewModel } from './pursuitWorkspaceModel';
import { pursuitResolvePath } from './resolvePursuitOpportunityId';
import {
  buildTenderReadiness,
  readinessTagColor,
  type TenderReadinessState,
} from './tenderReadiness';

const { Text } = Typography;

export type EpcTenderCockpitProps = {
  tender: Record<string, unknown>;
  bidDecisions?: Record<string, unknown>[];
};

export const EpcTenderCockpit: React.FC<EpcTenderCockpitProps> = ({
  tender,
  bidDecisions = [],
}) => {
  const navigate = useNavigate();
  const tenderId = String(tender.id || '');
  const opportunityId = String(tender.opportunity_id || tender.opportunityId || '');

  const approvedGoCount = bidDecisions.filter((b) => {
    const outcome = String(b.outcome || b.decision || '').toUpperCase();
    const status = String(b.status || '').toLowerCase();
    return status === 'approved' && (outcome === 'GO' || outcome === 'CONDITIONAL');
  }).length;
  const pendingApprovalCount = bidDecisions.filter(
    (b) => String(b.status || '').toLowerCase() === 'submitted'
  ).length;

  const readiness = useMemo(
    () =>
      buildTenderReadiness(tender, {
        bidDecisionCount: bidDecisions.length,
        approvedGoCount,
        pendingApprovalCount,
      }),
    [tender, bidDecisions.length, approvedGoCount, pendingApprovalCount]
  );

  const ctxQ = useQuery({
    queryKey: ['commercial-lifecycle-context', 'epc_tender', tenderId],
    queryFn: () => commercialLifecycleService.getContext('epc_tender', tenderId),
    enabled: Boolean(tenderId),
    staleTime: 30_000,
    retry: false,
  });

  const nextActions = useMemo(() => {
    if (!ctxQ.data) return [];
    return buildPursuitViewModel({
      opportunityId: opportunityId || '—',
      opportunity: null,
      context: ctxQ.data,
      definition: undefined,
      tenders: [tender],
      contracts: [],
    }).nextActions;
  }, [ctxQ.data, opportunityId, tender]);

  const deadlineBlocked = readiness.groups.find((g) => g.name === 'Deadlines')?.state === 'BLOCKED';

  return (
    <Card
      size="small"
      title={
        <Space wrap>
          <span>Tender cockpit</span>
          <Tag color={readinessTagColor(readiness.overall)}>{readiness.overall}</Tag>
        </Space>
      }
      style={{ marginBottom: 16 }}
      extra={
        <Space wrap>
          {tenderId ? (
            <Button
              size="small"
              onClick={() => navigate(pursuitResolvePath('epc_tender', tenderId))}
            >
              Pursuit
            </Button>
          ) : null}
          <Button size="small" onClick={() => navigate('/epc/commercial/bid-decisions')}>
            Bid decisions
          </Button>
        </Space>
      }
    >
      {deadlineBlocked ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Deadline blocked"
          description="One or more due dates have passed while the tender is still open."
        />
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <DescriptionsLite tender={tender} />
          <List
            size="small"
            style={{ marginTop: 12 }}
            header={<Text strong>Readiness groups</Text>}
            dataSource={readiness.groups}
            renderItem={(g) => (
              <List.Item>
                <List.Item.Meta
                  title={
                    <Space>
                      <Text>{g.name}</Text>
                      <Tag color={readinessTagColor(g.state as TenderReadinessState)}>{g.state}</Tag>
                    </Space>
                  }
                  description={
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {g.checks.map((c) => (
                        <li key={c.id}>
                          {c.label}: {c.state}
                          {c.detail ? ` — ${c.detail}` : ''}
                        </li>
                      ))}
                    </ul>
                  }
                />
              </List.Item>
            )}
          />
        </Col>
        <Col xs={24} lg={10}>
          <Card size="small" title="ECLE next actions">
            {ctxQ.isError ? (
              <Alert type="warning" showIcon message="Lifecycle context unavailable" />
            ) : (
              <PursuitNextActions
                actions={nextActions}
                onSelect={(a) => {
                  if (a.toStageKey.includes('BID')) {
                    navigate('/epc/commercial/bid-decisions');
                    return;
                  }
                  if (opportunityId) {
                    navigate(pursuitResolvePath('epc_tender', tenderId));
                    return;
                  }
                  navigate('/epc/commercial/tenders');
                }}
              />
            )}
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

function DescriptionsLite({ tender }: { tender: Record<string, unknown> }) {
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Text>
        Bid due:{' '}
        {tender.bid_due_at ? formatDate(String(tender.bid_due_at)) : '—'}
      </Text>
      <Text>
        Submission due:{' '}
        {tender.submission_due_at ? formatDate(String(tender.submission_due_at)) : '—'}
      </Text>
      <Text>
        Model: {String(tender.commercial_model || '—')} · Currency:{' '}
        {String(tender.currency || '—')}
      </Text>
    </Space>
  );
}

export default EpcTenderCockpit;
